import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import styles from "./SubscriptionSelection.module.scss";
import { StripePriceAnonymous } from "@switcherstudio/switcher-api-client";

interface FrequencyOption {
    plan: StripePriceAnonymous;
    label: string;
    pricePerMonth: number;
    savings?: number;
}

/** Length of a plan's billing period, in months */
function getIntervalMonths(plan: StripePriceAnonymous): number {
    const count = plan.RecurringIntervalCount ?? 1;
    return plan.RecurringInterval === "year" ? count * 12 : count;
}

function getLabel(plan: StripePriceAnonymous, t: TFunction): string {
    const count = plan.RecurringIntervalCount ?? 1;

    if (count > 1) {
        // need to handle quarterly and semiannual plans
        if (plan.RecurringInterval === "month") {
            if (count === 3) return t("quarterly");
            if (count === 6) return t("semiannual");
        }

        return t("every-count-interval", {
            count,
            interval: plan.RecurringInterval
        });
    }

    return t(plan.RecurringInterval);
}

function getMonthlyEquivalent(plan: StripePriceAnonymous): number {
    return (plan.Amount ?? 0) / 100 / getIntervalMonths(plan);
}

/** Longest billing period first */
function getSortOrder(plan: StripePriceAnonymous): number {
    return -getIntervalMonths(plan);
}

interface SubscriptionSelectionProps {
    plans: StripePriceAnonymous[];
    selectedPlan: StripePriceAnonymous | undefined;
    onChange: (plan: StripePriceAnonymous) => void;
}

export const SubscriptionSelection: React.FC<SubscriptionSelectionProps> = ({
    plans,
    selectedPlan,
    onChange
}) => {
    const { t } = useTranslation("getting-started");

    const options = useMemo<FrequencyOption[]>(() => {
        const monthlyPlan = plans.find(
            (p) =>
                p.RecurringInterval === "month" &&
                (p.RecurringIntervalCount ?? 1) === 1
        );
        const monthlyRate = monthlyPlan
            ? (monthlyPlan.Amount ?? 0) / 100
            : undefined;

        return [...plans]
            .sort((a, b) => getSortOrder(a) - getSortOrder(b))
            .map((plan) => {
                const pricePerMonth = getMonthlyEquivalent(plan);
                const savings =
                    monthlyRate && pricePerMonth < monthlyRate
                        ? Math.round((1 - pricePerMonth / monthlyRate) * 100)
                        : undefined;
                return {
                    plan,
                    label: getLabel(plan, t),
                    pricePerMonth,
                    savings
                };
            });
    }, [plans, t]);

    return (
        <div className={styles["subscription-selection"]}>
            {options.map((option) => {
                const isSelected = selectedPlan?.Id === option.plan.Id;
                return (
                    <label
                        key={option.plan.Id}
                        className={`${styles["option"]} ${isSelected ? styles["option--selected"] : ""}`}
                        htmlFor={`frequency-${option.plan.Id}`}
                    >
                        <span className={styles["option__label"]}>
                            {option.label}
                        </span>
                        <span className={styles["option__pricing"]}>
                            <span className={styles["option__price"]}>
                                ${Math.round(option.pricePerMonth)}
                                <span className={styles["option__per-month"]}>
                                    {t("subscription:per-month")}
                                </span>
                            </span>
                            {option.savings !== undefined && (
                                <span className={styles["option__savings"]}>
                                    {t("subscription:percent-savings", {
                                        percent: option.savings
                                    })}
                                </span>
                            )}
                        </span>
                        <input
                            type="radio"
                            id={`frequency-${option.plan.Id}`}
                            name="payment-frequency"
                            value={option.plan.Id}
                            checked={isSelected}
                            onChange={() => onChange(option.plan)}
                            className={styles["option__radio"]}
                        />
                        <span className={styles["option__radio-custom"]} />
                    </label>
                );
            })}
        </div>
    );
};
